import { DeviceSettings, InventorySettings } from 'api/interfaces'
import { UploadedImage } from 'components/Basic/AcImageUploader'
import { UploadedVideo } from 'components/Basic/AcVideoUploader'
import { displayAdSizes } from 'utils/imageUtils'

import { ChannelDraftBase, CreativeLinkMode, CreativeLinks, InternetChannelDraft, QuickCampaignChannel, QuickCampaignChannels, TvChannelDraft, creativeUrlsOf } from './quickCampaignModels'

export interface InternetFieldUpdate {
  budget?: number
  impressionFrequency?: number
  targetCPM?: number
  inventorySettings?: Partial<InventorySettings>
  deviceSettings?: Partial<DeviceSettings>
}

export type QuickCampaignChannelsAction =
  | { type: 'setChannelSelected'; channel: QuickCampaignChannel; isSelected: boolean }
  | { type: 'updateInternetFields'; fields: InternetFieldUpdate }
  | { type: 'setTvBudget'; budget: number }
  | { type: 'creativesUploaded'; channel: QuickCampaignChannel.Internet; files: UploadedImage[] }
  | { type: 'creativesUploaded'; channel: QuickCampaignChannel.Tv; files: UploadedVideo[] }
  | { type: 'creativeRemoved'; channel: QuickCampaignChannel; url: string }
  | { type: 'setUploading'; channel: QuickCampaignChannel; isUploading: boolean }
  | { type: 'setLinkMode'; channel: QuickCampaignChannel; mode: CreativeLinkMode }
  | { type: 'setSharedLink'; channel: QuickCampaignChannel; link: string }
  | { type: 'setCreativeLink'; channel: QuickCampaignChannel; creativeUrl: string; link: string }

type SharedChannelFields = Pick<ChannelDraftBase, 'isSelected' | 'isUploading' | 'links'>

const isValidDimensions = (image: UploadedImage) => displayAdSizes.some(size => size.width === image.width && size.height === image.height)

const isSameCreative = (existing: { url: string; fileName: string }, incoming: { url: string; fileName: string }) => existing.fileName === incoming.fileName || existing.url === incoming.url

const withoutReplaced = <T extends { url: string; fileName: string }>(existing: T[], incoming: T[]) => existing.filter(item => incoming.every(file => !isSameCreative(item, file)))

const patchChannel = (channels: QuickCampaignChannels, channel: QuickCampaignChannel, patch: Partial<SharedChannelFields>): QuickCampaignChannels => (channel === QuickCampaignChannel.Internet ? { ...channels, internet: { ...channels.internet, ...patch } } : { ...channels, tv: { ...channels.tv, ...patch } })

const linksOf = (channels: QuickCampaignChannels, channel: QuickCampaignChannel): CreativeLinks => (channel === QuickCampaignChannel.Internet ? channels.internet.links : channels.tv.links)

const withLinkEntries = (links: CreativeLinks, urls: string[]): CreativeLinks => {
  const missingUrls = urls.filter(url => !(url in links.linksByCreativeUrl))
  return missingUrls.length === 0 ? links : { ...links, linksByCreativeUrl: { ...links.linksByCreativeUrl, ...Object.fromEntries(missingUrls.map(url => [url, links.sharedLink])) } }
}

const withoutLinkEntries = (links: CreativeLinks, urls: string[]): CreativeLinks => ({ ...links, linksByCreativeUrl: Object.fromEntries(Object.entries(links.linksByCreativeUrl).filter(([url]) => !urls.includes(url))) })

const replacedUrlsOf = <T extends { url: string; fileName: string }>(existing: T[], incoming: T[]) => existing.filter(item => incoming.some(file => isSameCreative(item, file) && item.url !== file.url)).map(item => item.url)

const withUploadedLinks = (links: CreativeLinks, replacedUrls: string[], uploadedUrls: string[]): CreativeLinks => {
  const cleanedLinks = withoutLinkEntries(links, replacedUrls)
  return cleanedLinks.mode === CreativeLinkMode.Unique ? withLinkEntries(cleanedLinks, uploadedUrls) : cleanedLinks
}

const mergeImages = (internet: InternetChannelDraft, files: UploadedImage[]): InternetChannelDraft => ({
  ...internet,
  uploadedImages: [...withoutReplaced(internet.uploadedImages, files), ...files],
  invalidImages: [...withoutReplaced(internet.invalidImages, files), ...files.filter(file => !isValidDimensions(file))],
  links: withUploadedLinks(
    internet.links,
    replacedUrlsOf(internet.uploadedImages, files),
    files.filter(isValidDimensions).map(file => file.url)
  )
})

const mergeVideos = (tv: TvChannelDraft, files: UploadedVideo[]): TvChannelDraft => ({
  ...tv,
  uploadedVideos: [...withoutReplaced(tv.uploadedVideos, files), ...files],
  links: withUploadedLinks(
    tv.links,
    replacedUrlsOf(tv.uploadedVideos, files),
    files.map(file => file.url)
  )
})

const removeCreative = (channels: QuickCampaignChannels, channel: QuickCampaignChannel, url: string): QuickCampaignChannels =>
  channel === QuickCampaignChannel.Internet
    ? { ...channels, internet: { ...channels.internet, uploadedImages: channels.internet.uploadedImages.filter(image => image.url !== url), invalidImages: channels.internet.invalidImages.filter(image => image.url !== url), links: withoutLinkEntries(channels.internet.links, [url]) } }
    : { ...channels, tv: { ...channels.tv, uploadedVideos: channels.tv.uploadedVideos.filter(video => video.url !== url), links: withoutLinkEntries(channels.tv.links, [url]) } }

const changeLinkMode = (channels: QuickCampaignChannels, channel: QuickCampaignChannel, mode: CreativeLinkMode): QuickCampaignChannels => {
  const links = { ...linksOf(channels, channel), mode }
  return patchChannel(channels, channel, { links: mode === CreativeLinkMode.Unique ? withLinkEntries(links, creativeUrlsOf(channels, channel)) : links })
}

export const quickCampaignChannelsReducer = (state: QuickCampaignChannels, action: QuickCampaignChannelsAction): QuickCampaignChannels => {
  switch (action.type) {
    case 'setChannelSelected':
      return patchChannel(state, action.channel, { isSelected: action.isSelected })
    case 'updateInternetFields': {
      const { fields } = action
      return {
        ...state,
        internet: {
          ...state.internet,
          ...fields,
          inventorySettings: { ...state.internet.inventorySettings, ...fields.inventorySettings },
          deviceSettings: { ...state.internet.deviceSettings, ...fields.deviceSettings }
        }
      }
    }
    case 'setTvBudget':
      return { ...state, tv: { ...state.tv, budget: action.budget } }
    case 'creativesUploaded': {
      if (action.files.length === 0) {
        return state
      }
      if (action.channel === QuickCampaignChannel.Internet) {
        return { ...state, internet: mergeImages(state.internet, action.files) }
      }
      return { ...state, tv: mergeVideos(state.tv, action.files) }
    }
    case 'creativeRemoved':
      return removeCreative(state, action.channel, action.url)
    case 'setUploading':
      return patchChannel(state, action.channel, { isUploading: action.isUploading })
    case 'setLinkMode':
      return changeLinkMode(state, action.channel, action.mode)
    case 'setSharedLink':
      return patchChannel(state, action.channel, { links: { ...linksOf(state, action.channel), sharedLink: action.link } })
    case 'setCreativeLink': {
      const links = linksOf(state, action.channel)
      return patchChannel(state, action.channel, { links: { ...links, linksByCreativeUrl: { ...links.linksByCreativeUrl, [action.creativeUrl]: action.link } } })
    }
  }
}
